import { useState } from 'react';
import { Modal } from '@/components/atoms/Modal';
import { TabNav } from '@/components/molecules/TabNav';
import { changelog, CURRENT_VERSION } from '@/lib/infra/changelog';
import { gitLog, type GitLogEntry } from '@/lib/infra/gitLog';
import { getChannel, isPreviewChannel } from '@/lib/infra/storage';

interface UpdatesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_COLORS: Record<string, string> = {
  feat: 'bg-emerald-500/20 text-emerald-400',
  fix: 'bg-amber-500/20 text-amber-400',
  refactor: 'bg-blue-500/20 text-blue-400',
  docs: 'bg-purple-500/20 text-purple-400',
  test: 'bg-cyan-500/20 text-cyan-400',
  chore: 'bg-gray-500/20 text-gray-400',
  style: 'bg-pink-500/20 text-pink-400',
  perf: 'bg-orange-500/20 text-orange-400',
};

function getTypeBadgeClass(type: string): string {
  return TYPE_COLORS[type] ?? 'bg-gray-500/20 text-gray-400';
}

/** Group git log entries by date */
function groupByDate(entries: GitLogEntry[]): Map<string, GitLogEntry[]> {
  const map = new Map<string, GitLogEntry[]>();
  for (const entry of entries) {
    const group = map.get(entry.date);
    if (group) {
      group.push(entry);
    } else {
      map.set(entry.date, [entry]);
    }
  }
  return map;
}

const TABS = [
  { id: 'patch-notes', label: 'Patch Notes' },
  { id: 'activity', label: 'Activity Log' },
];

export function UpdatesModal({ isOpen, onClose }: UpdatesModalProps) {
  const [activeTab, setActiveTab] = useState('patch-notes');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Updates — ${CURRENT_VERSION}`}
      size="xl"
    >
      <div className="space-y-4">
        {/* Channel info card */}
        <div className="bg-muted/50 border-border rounded-lg border p-3">
          {isPreviewChannel() ? (
            <div className="flex items-center justify-between">
              <span className="text-sm">
                You are on <span className="font-semibold text-amber-400">{getChannel()}</span>
              </span>
              <a
                href="/"
                className="text-primary text-xs hover:underline"
              >
                Switch to stable
              </a>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-sm">Try new features early</span>
              <a
                href="/canary/"
                className="text-primary text-xs hover:underline"
              >
                Switch to canary
              </a>
            </div>
          )}
        </div>

        {/* Tab navigation */}
        <TabNav
          tabs={TABS}
          activeTab={activeTab}
          onChange={setActiveTab}
          variant="boxed"
          size="sm"
          fullWidth
        />

        {/* Tab content */}
        {activeTab === 'patch-notes' ? (
          <div className="space-y-6">
            {changelog.map((entry) => (
              <div key={entry.version}>
                <div className="mb-2 flex items-baseline gap-3">
                  <span className="bg-primary/15 text-primary rounded px-2 py-0.5 font-mono text-sm font-semibold">
                    {entry.version}
                  </span>
                  <span className="text-muted-foreground text-xs">{entry.date}</span>
                  <span className="text-sm font-medium">{entry.title}</span>
                </div>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  {entry.changes.map((change, i) => (
                    <li
                      key={i}
                      className="flex gap-2"
                    >
                      <span className="text-muted-foreground/50 select-none">-</span>
                      <span>{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-5">
            {gitLog.length === 0 ? (
              <p className="text-muted-foreground text-sm">No activity data available.</p>
            ) : (
              Array.from(groupByDate(gitLog)).map(([date, entries]) => (
                <div key={date}>
                  <h3 className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider">
                    {date}
                  </h3>
                  <div className="space-y-1.5">
                    {entries.map((entry) => (
                      <div
                        key={entry.hash}
                        className="flex items-start gap-2 text-sm"
                      >
                        <span
                          className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${getTypeBadgeClass(entry.type)}`}
                        >
                          {entry.type}
                        </span>
                        <span className="text-muted-foreground/60 mt-0.5 shrink-0 font-mono text-xs">
                          {entry.hash}
                        </span>
                        <span className="text-foreground/80">{entry.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
